/**
 * ELORÉNE storefront refinements — V14 AUTO NEW-PRODUCT SWATCH + PDP MEDIA SYNC (09 Sep 2026)
 * CRITICAL RULE: never read/write/replace product-card hrefs or navigate manually.
 * All product navigation stays 100% Horizon-native, identical to working V2.
 */
(() => {
  'use strict';

  const COLOR_MAP = {
    black:'#111111', white:'#f7f7f5', ivory:'#f3eee2', cream:'#eee4d0', oatmeal:'#d8cfbd',
    beige:'#d2bea0', nude:'#d7b39d', tan:'#b68962', camel:'#b67c50', taupe:'#9a8274',
    brown:'#6b4737', chocolate:'#4a2c24', mocha:'#866556', espresso:'#3c2a24', coffee:'#68483b',
    grey:'#9b9b99', gray:'#9b9b99', charcoal:'#4a4a49', silver:'#c3c5c7', gold:'#c5a35f',
    red:'#c81936', cherry:'#bd1230', cherryred:'#bd1230', scarlet:'#cf2038', crimson:'#a51e38',
    burgundy:'#70162c', wine:'#742b40', maroon:'#651f2f', berry:'#7b3654', rust:'#a65438',
    pink:'#e6a4b6', babypink:'#efc5cf', lightpink:'#efc5cf', blush:'#e7b8bf', rose:'#c77b89', hotpink:'#dc4d86',
    fuchsia:'#d31975', magenta:'#c51e73', mauve:'#9f6e87', coral:'#e37c6e', salmon:'#e79a8e',
    orange:'#dd7432', peach:'#efb29a', yellow:'#e3c64c', lemon:'#e7dc77', mustard:'#b8942d',
    blue:'#436e9f', navy:'#1e2d4b', cobalt:'#2752ac', royalblue:'#2f50a0', sky:'#9fc5dc',
    skyblue:'#9fc5dc', babyblue:'#abc9df', denim:'#5f7894', teal:'#2b7a77', turquoise:'#50a5a6', aqua:'#77bdc3',
    green:'#4e7752', olive:'#767244', sage:'#9da78c', emerald:'#176947', forestgreen:'#29563a',
    mint:'#aacdb6', khaki:'#8d8868', lime:'#a2bd55',
    purple:'#6d4b85', plum:'#61405f', lilac:'#b7a0c8', lavender:'#b8a7cf', violet:'#76538c',
    '블랙':'#111111','검정':'#111111','검정색':'#111111','화이트':'#f7f7f5','흰색':'#f7f7f5',
    '아이보리':'#f3eee2','크림':'#eee4d0','베이지':'#d2bea0','누드':'#d7b39d','브라운':'#6b4737','갈색':'#6b4737',
    '그레이':'#9b9b99','회색':'#9b9b99','실버':'#c3c5c7','골드':'#c5a35f','레드':'#c81936','빨강':'#c81936','빨간색':'#c81936',
    '버건디':'#70162c','와인':'#742b40','핑크':'#e6a4b6','분홍':'#e6a4b6','분홍색':'#e6a4b6','오렌지':'#dd7432','주황':'#dd7432',
    '옐로우':'#e3c64c','노랑':'#e3c64c','블루':'#436e9f','파랑':'#436e9f','파란색':'#436e9f','네이비':'#1e2d4b',
    '그린':'#4e7752','초록':'#4e7752','초록색':'#4e7752','올리브':'#767244','세이지':'#9da78c','퍼플':'#6d4b85','보라':'#6d4b85','보라색':'#6d4b85','라벤더':'#b8a7cf'
  };

  const cache = new Map();
  const STORAGE_KEY = 'elorenePaletteCacheV14';
  let persistedPaletteCache = {};
  let persistTimer = 0;
  try { persistedPaletteCache = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}; } catch (_) {}

  const compact = (value='') => String(value).toLowerCase().trim().replace(/[\s_\-/]+/g,'');
  const paletteCacheKey = (url='') => {
    try {
      const u = new URL(normaliseUrl(url), location.href);
      u.searchParams.delete('width');
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      return `${u.host}${u.pathname}`;
    } catch (_) { return String(url || '').replace(/[?&](?:width|height|crop)=[^&]*/g, ''); }
  };
  function schedulePalettePersist(key,palette){
    if(!key || !palette?.length) return;
    persistedPaletteCache[key] = palette.slice(0,3);
    clearTimeout(persistTimer);
    persistTimer = setTimeout(()=>{
      try {
        const entries = Object.entries(persistedPaletteCache);
        if(entries.length > 1800) persistedPaletteCache = Object.fromEntries(entries.slice(-1400));
        localStorage.setItem(STORAGE_KEY, JSON.stringify(persistedPaletteCache));
      } catch (_) {}
    }, 500);
  }
  const isSupplierCode = (value='') => /^(?:cs|c|cl|col)[\s_\-]*\d+[a-z]?$/i.test(String(value).trim());
  const normaliseUrl = (url='') => url.startsWith('//') ? `${location.protocol}${url}` : url;

  function hexToRgb(hex) {
    const h=String(hex||'').replace('#','');
    if (h.length===3) return h.split('').map(x=>parseInt(x+x,16));
    if (h.length!==6) return [128,128,128];
    return [parseInt(h.slice(0,2),16),parseInt(h.slice(2,4),16),parseInt(h.slice(4,6),16)];
  }
  function rgbToHex(r,g,b) {
    const p=n=>Math.max(0,Math.min(255,Math.round(n))).toString(16).padStart(2,'0');
    return `#${p(r)}${p(g)}${p(b)}`;
  }
  function rgbToHsv(r,g,b) {
    r/=255; g/=255; b/=255;
    const max=Math.max(r,g,b), min=Math.min(r,g,b), d=max-min;
    let h=0;
    if (d) {
      if (max===r) h=((g-b)/d)%6;
      else if (max===g) h=(b-r)/d+2;
      else h=(r-g)/d+4;
      h*=60; if(h<0) h+=360;
    }
    return [h,max===0?0:d/max,max];
  }
  function dist(a,b){const dr=a[0]-b[0],dg=a[1]-b[1],db=a[2]-b[2];return Math.sqrt(2*dr*dr+4*dg*dg+3*db*db);}
  function hueDist(a,b){const d=Math.abs(a-b); return Math.min(d,360-d);}
  function median(a){const b=[...a].sort((x,y)=>x-y); return b[Math.floor(b.length/2)]??0;}

  function resolveNamed(value) {
    const raw=String(value||'').toLowerCase().trim();
    if (!raw || raw==='__visual__' || isSupplierCode(raw)) return null;
    const key=compact(raw);
    if (COLOR_MAP[key]) return [COLOR_MAP[key]];
    const hits=[];
    for (const name of Object.keys(COLOR_MAP).sort((a,b)=>b.length-a.length)) {
      if (raw.includes(name)) {
        const c=COLOR_MAP[name]; if(!hits.includes(c)) hits.push(c);
        if(hits.length===3) break;
      }
    }
    if (hits.length) return hits;
    if (window.CSS?.supports?.('color',raw)) return [raw];
    return null;
  }

  function gradient(colors) {
    const p=[...new Set((colors||[]).filter(Boolean))].slice(0,3);
    if(!p.length) return null;
    if(p.length===1) return {color:p[0],image:'none'};
    const step=100/p.length;
    return {color:'transparent', image:`conic-gradient(from -90deg, ${p.map((c,i)=>`${c} ${i*step}% ${(i+1)*step}%`).join(',')})`};
  }
  function applyPalette(el,palette,{productPage=false}={}) {
    const g=gradient(palette); if(!g||!el) return;
    // V12: use the same CSS custom-property pipeline on PLP and PDP.
    // V11 wrote collection gradients to inline background-image, but the PLP CSS
    // had an !important var(--elorene-swatch-bg) rule, so multi-colour gradients
    // were silently overridden. That is why PDP looked right while PLP did not.
    el.style.setProperty('--elorene-swatch-color',g.color);
    el.style.setProperty('--elorene-swatch-bg',g.image);
  }

  async function inferPalette(url) {
    url=normaliseUrl(url); if(!url) return null;
    const key=paletteCacheKey(url);
    if(cache.has(key)) return cache.get(key);
    const stored=persistedPaletteCache[key];
    if(Array.isArray(stored)&&stored.length){ cache.set(key,Promise.resolve(stored)); return stored; }
    const promise=new Promise(resolve=>{
      const img=new Image(); img.crossOrigin='anonymous'; img.decoding='async';
      img.onload=()=>{
        try {
          const s=32,c=document.createElement('canvas'); c.width=s;c.height=s;
          const ctx=c.getContext('2d',{willReadFrequently:true}); ctx.drawImage(img,0,0,s,s);
          const d=ctx.getImageData(0,0,s,s).data;
          const px=(x,y)=>{const i=(y*s+x)*4;return[d[i],d[i+1],d[i+2]];};
          const border=[];
          for(let i=2;i<s-2;i+=5) border.push(px(i,2),px(i,s-3),px(2,i),px(s-3,i));
          const bg=[median(border.map(p=>p[0])),median(border.map(p=>p[1])),median(border.map(p=>p[2]))];
          const bins=new Map(); let total=0;
          for(let y=5;y<s-3;y++) for(let x=4;x<s-4;x++) {
            const i=(y*s+x)*4; if(d[i+3]<220) continue;
            const rgb=[d[i],d[i+1],d[i+2]], [h,sv,v]=rgbToHsv(...rgb);
            if(dist(rgb,bg)<34 && (v>.4||sv<.2)) continue;
            if(v>.992&&sv<.035&&dist(rgb,bg)<72) continue;
            const nx=x/(s-1),ny=y/(s-1);
            let w=.7+(1-Math.min(1,Math.abs(nx-.5)*2))*.8+Math.max(0,ny-.25)*.7;
            const skin=h>=4&&h<=52&&sv>=.16&&sv<=.72&&v>=.42;
            if(skin&&ny<.66) w*=.14;
            const key=sv<.13?`n-${Math.round(v*9)}`:`c-${Math.round(h/15)%24}-${sv>.55?2:sv>.30?1:0}-${v>.74?2:v>.42?1:0}`;
            const b=bins.get(key)||{score:0,r:0,g:0,b:0,w:0};
            b.score+=w;b.r+=rgb[0]*w;b.g+=rgb[1]*w;b.b+=rgb[2]*w;b.w+=w;bins.set(key,b);total+=w;
          }
          let cs=[...bins.values()].map(b=>{const rgb=[b.r/b.w,b.g/b.w,b.b/b.w],hsv=rgbToHsv(...rgb);return{score:b.score,rgb,h:hsv[0],s:hsv[1],v:hsv[2],hex:rgbToHex(...rgb)}}).sort((a,b)=>b.score-a.score);
          if(!cs.length) return resolve(null);
          const merged=[];
          for(const c0 of cs){
            const m=merged.find(x=>(x.s<.15&&c0.s<.15)?Math.abs(x.v-c0.v)<.22:(hueDist(x.h,c0.h)<18&&Math.abs(x.v-c0.v)<.28));
            if(m){const sum=m.score+c0.score;m.rgb=[0,1,2].map(i=>(m.rgb[i]*m.score+c0.rgb[i]*c0.score)/sum);m.score=sum;const hsv=rgbToHsv(...m.rgb);m.h=hsv[0];m.s=hsv[1];m.v=hsv[2];m.hex=rgbToHex(...m.rgb);} else merged.push({...c0});
          }
          merged.sort((a,b)=>b.score-a.score);
          const chrom=merged.filter(c=>c.s>=.14).reduce((a,c)=>a+c.score,0);
          let pool=chrom/Math.max(total,1)>.22?merged.filter(c=>c.s>=.14||(c.v<.2&&c.score>merged[0].score*.3)):merged;
          if(!pool.length) pool=merged;
          const picked=[]; const top=pool[0].score;
          for(const c0 of pool){
            if(!picked.length){picked.push(c0);continue;}
            if(c0.score/top<(picked.length===1?.18:.12)) continue;
            if(c0.score/Math.max(total,1)<.025) continue;
            if(!picked.every(p=>dist(c0.rgb,p.rgb)>72&&(c0.s<.14||p.s<.14||hueDist(c0.h,p.h)>20))) continue;
            picked.push(c0); if(picked.length===3) break;
          }
          if(picked.length>1&&merged[0].score/Math.max(total,1)>.58&&picked[1].score/merged[0].score<.32) picked.splice(1);
          const palette=picked.map(x=>x.hex);
          schedulePalettePersist(key,palette);
          resolve(palette);
        } catch(e){ resolve(null); }
      };
      img.onerror=()=>resolve(null); img.src=url;
    });
    cache.set(key,promise); return promise;
  }

  // V13: start every collection swatch immediately.
  // Cached/named colours paint synchronously; image-derived palettes are refined in parallel.
  const paletteQueue=[];
  const queuedDots=new WeakSet();
  let activePaletteJobs=0;
  const MAX_PALETTE_JOBS=10;

  function pumpPaletteQueue(){
    while(activePaletteJobs<MAX_PALETTE_JOBS && paletteQueue.length){
      const dot=paletteQueue.shift();
      if(!dot?.isConnected || dot.dataset.eloreneReady==='true') continue;
      activePaletteJobs++;
      Promise.resolve().then(()=>paintDot(dot)).finally(()=>{
        activePaletteJobs--;
        pumpPaletteQueue();
      });
    }
  }
  function queueDot(dot){
    if(!dot || dot.dataset.eloreneReady==='true' || queuedDots.has(dot)) return;
    queuedDots.add(dot);
    paletteQueue.push(dot);
    pumpPaletteQueue();
  }

  function elements(root,selector){
    const list=[];
    if(root?.matches?.(selector)) list.push(root);
    root?.querySelectorAll?.(selector)?.forEach(el=>list.push(el));
    return list;
  }
  function slideImage(slide){return slide?.querySelector?.('img.product-media__image, img');}
  function imageUrl(img){if(!img)return '';return normaliseUrl(img.currentSrc||img.src||img.getAttribute('src')||'');}
  function slideId(slide){return String(slide?.getAttribute?.('slide-id')||slide?.dataset?.slideId||'');}

  async function paintDot(dot){
    if(!dot || dot.dataset.eloreneReady==='true') return;
    const raw=dot.dataset.colorName||'';
    const sample=dot.dataset.colorSampleUrl||dot.dataset.imageUrl||'';
    const named=resolveNamed(raw);
    const shopifyColor=dot.dataset.shopifySwatchColor;

    // Give the shopper a real colour immediately instead of an empty/loading dot.
    if(named?.length) applyPalette(dot,named);
    else if(shopifyColor) applyPalette(dot,[shopifyColor]);
    else applyPalette(dot,['#b8b6b0']);

    // If this image was analysed on an earlier visit, the correct palette is instant.
    if(sample){
      const key=paletteCacheKey(sample);
      const stored=persistedPaletteCache[key];
      if(Array.isArray(stored)&&stored.length){
        applyPalette(dot,stored);
        dot.dataset.eloreneReady='true';
        return;
      }
      // Otherwise refine from the real variant image immediately in the background.
      const palette=await inferPalette(sample);
      if(palette?.length) applyPalette(dot,palette);
    }
    dot.dataset.eloreneReady='true';
  }

  function observeCardColors(root=document){
    elements(root,'product-card').forEach(card=>{
      if(card.dataset.elorenePaletteObserved==='true') return;
      const dots=card.querySelectorAll('.elorene-card-swatch');
      if(!dots.length) return;
      card.dataset.elorenePaletteObserved='true';
      // V13 intentionally starts ALL colour work at page open; no viewport wait.
      dots.forEach(queueDot);
    });
  }

  function selectSwatch(card,button){
    card?.querySelectorAll('.elorene-card-swatch').forEach(s=>{
      const on=s===button;
      s.classList.toggle('is-selected',on);
      s.setAttribute('aria-pressed',on?'true':'false');
    });
  }
  function swapToSwatch(button){
    const card=button.closest('product-card'); if(!card) return;
    const mediaId=String(button.dataset.mediaId||'');
    const slideshow=card.querySelector('.card-gallery slideshow-component');
    if(mediaId && slideshow && typeof slideshow.select==='function'){
      const target=card.querySelector(`.card-gallery slideshow-slide[slide-id="${CSS.escape(mediaId)}"]`);
      if(target){
        slideshow.select({id:mediaId},undefined,{animate:true});
        selectSwatch(card,button); return;
      }
    }
    // Visual fallback only. Product hrefs remain Horizon-native and untouched.
    const url=button.dataset.imageUrl;
    if(url){
      const active=card.querySelector('.card-gallery slideshow-slide[aria-hidden="false"] img.product-media__image')||card.querySelector('.card-gallery img.product-media__image');
      if(active){active.srcset='';active.src=url;if(button.dataset.imageAlt)active.alt=button.dataset.imageAlt;}
    }
    selectSwatch(card,button);
  }
  function bindDots(root=document){
    root.querySelectorAll?.('.elorene-card-swatch:not([data-elorene-bound])').forEach(button=>{
      button.dataset.eloreneBound='true';
      button.addEventListener('click',e=>{e.preventDefault();e.stopPropagation();swapToSwatch(button);});
      button.addEventListener('pointerdown',e=>e.stopPropagation());
    });
  }

  function syncSwatchToGallery(card,preferredId=''){
    const dots=[...card.querySelectorAll('.elorene-card-swatch')]; if(!dots.length) return;
    const slides=[...card.querySelectorAll('.card-gallery slideshow-slide')];
    const active=slides.find(s=>s.getAttribute('aria-hidden')==='false');
    const id=String(preferredId||slideId(active)||'');
    let match=dots.find(dot=>String(dot.dataset.mediaId||'')===id);
    if(!match && active){
      const activeIndex=slides.indexOf(active);
      let best=null,bestIndex=-1;
      for(const dot of dots){
        const target=slides.findIndex(s=>slideId(s)===String(dot.dataset.mediaId||''));
        if(target>=0 && target<=activeIndex && target>=bestIndex){best=dot;bestIndex=target;}
      }
      match=best;
    }
    if(match) selectSwatch(card,match);
  }
  function bindGallerySync(root=document){
    elements(root,'product-card:not([data-elorene-sync])').forEach(card=>{
      if(!card.querySelector('.elorene-card-swatch')) return;
      card.dataset.eloreneSync='true';
      // Horizon reliably emits slideshow:select for arrow clicks, drag and programmatic selection.
      // No per-slide MutationObservers: V11 created hundreds of observers on large collections.
      card.addEventListener('slideshow:select',e=>{
        const id=String(e.detail?.id||e.detail?.slide?.getAttribute?.('slide-id')||'');
        syncSwatchToGallery(card,id);
      });
    });
  }

  function enhanceProductColors(root=document){
    root.querySelectorAll?.('#MainContent[data-template^="product"] variant-picker fieldset.variant-option--buttons:not(.variant-option--swatches)').forEach(fieldset=>{
      const inputs=[...fieldset.querySelectorAll('input[data-option-name]')]; if(!inputs.length) return;
      const name=(inputs[0].dataset.optionName||'').toLowerCase();
      const coded=inputs.every(i=>isSupplierCode(i.value));
      const tagged=inputs.some(i=>i.dataset.eloreneColorValue==='true');
      if(!name.includes('color')&&!name.includes('colour')&&!coded&&!tagged) return;
      fieldset.classList.add('elorene-color-option');
      inputs.forEach(input=>{
        const label=input.closest('label'); if(!label || label.dataset.elorenePainted==='true') return;
        label.dataset.elorenePainted='true';
        const named=resolveNamed(input.value),sample=input.dataset.colorImageUrl;
        applyPalette(label,named||['#b8b6b0'],{productPage:true});
        label.title=input.value;
        if(sample){
          inferPalette(sample).then(palette=>{
            if(label.isConnected&&palette?.length) applyPalette(label,palette,{productPage:true});
          });
        }
      });
    });
  }

  function canonicalImagePath(url=''){
    try { return new URL(normaliseUrl(url),location.href).pathname; } catch (_) { return String(url||'').split('?')[0]; }
  }

  function selectPdpMedia(mediaId='', imageUrl='', attempt=0){
    const main=document.querySelector('#MainContent[data-template^="product"]');
    const gallery=main?.querySelector('media-gallery.media-gallery--carousel, media-gallery');
    const slideshow=gallery?.querySelector('slideshow-component');
    if(!gallery||!slideshow){
      if(attempt<4) setTimeout(()=>selectPdpMedia(mediaId,imageUrl,attempt+1),60*(attempt+1));
      return;
    }
    const slides=[...slideshow.querySelectorAll('slideshow-slide')];
    let index=-1;
    if(mediaId){
      index=slides.findIndex(slide=>String(slide.getAttribute('slide-id')||'')===String(mediaId));
    }
    if(index<0&&imageUrl){
      const path=canonicalImagePath(imageUrl);
      index=slides.findIndex(slide=>{
        const img=slide.querySelector('img.product-media__image, img');
        return img&&canonicalImagePath(img.currentSrc||img.src||'')===path;
      });
    }
    if(index>=0&&typeof slideshow.select==='function'){
      slideshow.select(index,undefined,{animate:false});
      return;
    }
    // Last-resort immediate visual swap while Shopify finishes its native section refresh.
    if(imageUrl){
      const active=slides.find(s=>s.getAttribute('aria-hidden')==='false')||slides[0];
      const img=active?.querySelector('img.product-media__image, img');
      if(img){ img.removeAttribute('srcset'); img.src=imageUrl; }
    }
    if(attempt<4) setTimeout(()=>selectPdpMedia(mediaId,imageUrl,attempt+1),80*(attempt+1));
  }

  function bindPdpColorMediaSync(root=document){
    root.querySelectorAll?.('#MainContent[data-template^="product"] variant-picker:not([data-elorene-media-sync])').forEach(picker=>{
      picker.dataset.eloreneMediaSync='true';
      picker.addEventListener('change',event=>{
        const input=event.target;
        if(!(input instanceof HTMLInputElement)||!input.closest('.elorene-color-option')) return;
        selectPdpMedia(input.dataset.colorMediaId||'',input.dataset.colorImageUrl||'');
      });
    });
  }

  function bindNativeProductSelectMediaSync(){
    const main=document.getElementById('MainContent');
    if(!main||main.dataset.eloreneProductSelectSync==='true') return;
    main.dataset.eloreneProductSelectSync='true';
    main.addEventListener('shopify:product:select',event=>{
      if(!(event.target instanceof Element)||event.target.closest('product-card')) return;
      const selected=event.target.querySelector?.('.elorene-color-option input:checked');
      const fallbackId=selected?.dataset?.colorMediaId||'';
      const fallbackUrl=selected?.dataset?.colorImageUrl||'';
      if(!event.promise?.then) return;
      event.promise.then(({detail}={})=>{
        const mediaId=detail?.resource?.featured_media?.id||fallbackId;
        const mediaUrl=detail?.resource?.featured_media?.preview_image?.src||fallbackUrl;
        requestAnimationFrame(()=>selectPdpMedia(mediaId,mediaUrl));
      }).catch(()=>{});
    });
  }

  function run(root=document){
    bindDots(root);
    bindGallerySync(root);
    observeCardColors(root);
    enhanceProductColors(root);
    bindPdpColorMediaSync(root);
    bindNativeProductSelectMediaSync();
  }

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',()=>run(),{once:true});
  else run();

  // Keep AJAX filter/sort renders working, but avoid observing every attribute/media change.
  const main=document.getElementById('MainContent');
  if(main){
    let scheduled=false;
    const pending=[];
    const observer=new MutationObserver(mutations=>{
      let relevant=false;
      for(const m of mutations){
        for(const n of m.addedNodes){
          if(n.nodeType!==1) continue;
          if(n.matches?.('product-card, variant-picker') || n.querySelector?.('product-card, variant-picker')){
            pending.push(n); relevant=true;
          }
        }
      }
      if(relevant&&!scheduled){
        scheduled=true;
        requestAnimationFrame(()=>{scheduled=false;while(pending.length)run(pending.shift());});
      }
    });
    observer.observe(main,{childList:true,subtree:true});
  }
})();


/** ELORÉNE V16 header/drawer finalization — 10 Sep 2026 */
(() => {
  'use strict';

  const CURRENCY_RE = /(?:^|\s)(USD|EUR|GBP|AUD|CAD|NZD|JPY|KRW|CNY|HKD|SGD|CHF|SEK|NOK|DKK|PLN|CZK|HUF|AED|SAR|THB|TWD|MYR|IDR|PHP|INR|BRL|MXN|ZAR)(?:\s|$)/i;
  const APP_HINT_RE = /(currency|converter|switcher|nova|booster|boost)/i;

  function visible(el) {
    if (!(el instanceof Element)) return false;
    const r = el.getBoundingClientRect();
    const cs = getComputedStyle(el);
    return r.width > 24 && r.height > 18 && r.width < 360 && r.height < 180 && cs.display !== 'none' && cs.visibility !== 'hidden';
  }

  function markSaleMenu() {
    document.querySelectorAll('#header-component .menu-drawer__menu-item--mainlist').forEach((link) => {
      if ((link.textContent || '').trim().toUpperCase() === 'SALE') link.classList.add('elorene-sale-link');
    });
  }

  function candidateScore(el) {
    if (!visible(el)) return -1;
    if (el.closest('#header-component, #search-modal, cart-drawer, dialog')) return -1;
    const text = (el.textContent || el.value || '').replace(/\s+/g, ' ').trim();
    if (!CURRENCY_RE.test(text)) return -1;
    const key = `${el.id || ''} ${typeof el.className === 'string' ? el.className : ''}`;
    const cs = getComputedStyle(el);
    let score = 1;
    if (APP_HINT_RE.test(key)) score += 8;
    if (el.matches('button,[role="button"],select')) score += 4;
    if (el.querySelector?.('img,svg')) score += 2;
    if (cs.position === 'fixed' || cs.position === 'absolute') score += 4;
    const r = el.getBoundingClientRect();
    if (r.top < 180) score += 2; // current Nova placement is normally near the header before we move it
    return score;
  }

  function bestCurrencyCandidate() {
    const preferred = [
      ...document.querySelectorAll('[class*="currency" i], [id*="currency" i], [class*="nova" i], [id*="nova" i], [class*="converter" i], [id*="converter" i], [class*="switcher" i], [id*="switcher" i]')
    ];
    const controls = [...document.querySelectorAll('button,[role="button"],select')];
    let best = null, bestScore = -1;
    for (const el of [...new Set([...preferred, ...controls])]) {
      const score = candidateScore(el);
      if (score > bestScore) { best = el; bestScore = score; }
    }
    return bestScore >= 4 ? best : null;
  }

  function currencyRoot(el) {
    let node = el;
    let fallback = el;
    for (let i = 0; node && node !== document.body && i < 7; i++, node = node.parentElement) {
      const r = node.getBoundingClientRect();
      const cs = getComputedStyle(node);
      const key = `${node.id || ''} ${typeof node.className === 'string' ? node.className : ''}`;
      const compact = r.width > 24 && r.height > 18 && r.width < 360 && r.height < 190;
      if (compact) fallback = node;
      if (compact && APP_HINT_RE.test(key)) return node;
      if (compact && (cs.position === 'fixed' || cs.position === 'absolute')) return node;
    }
    return fallback;
  }

  function pinElement(el) {
    if (!el || el.dataset?.eloreneCurrencyPinned === 'true') return false;
    el.setAttribute('data-elorene-currency-pinned', 'true');
    const important = (name, value) => el.style.setProperty(name, value, 'important');
    important('position', 'fixed');
    important('left', '50%');
    important('right', 'auto');
    important('top', 'auto');
    important('bottom', 'max(18px, env(safe-area-inset-bottom))');
    important('transform', 'translateX(-50%)');
    important('margin', '0');
    important('z-index', '80');
    return true;
  }

  function pinCurrencySwitcher() {
    // Prefer Shopify's native Markets/country selector. In V16 this lived inside
    // #header-component, so the generic detector intentionally skipped it and left it top-left.
    const native = document.querySelector('#header-component dropdown-localization-component.dropdown-localization');
    if (native) {
      pinElement(native);
      return;
    }

    // Some app versions render in an iframe; pin the iframe itself when identifiable.
    for (const frame of document.querySelectorAll('iframe')) {
      const key = `${frame.src || ''} ${frame.title || ''} ${frame.name || ''}`;
      if (APP_HINT_RE.test(key) && visible(frame)) {
        pinElement(frame);
        return;
      }
    }
    const candidate = bestCurrencyCandidate();
    if (candidate) pinElement(currencyRoot(candidate));
  }

  function runV16HeaderFixes() {
    markSaleMenu();
    pinCurrencySwitcher();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', runV16HeaderFixes, { once: true });
  else runV16HeaderFixes();

  let attempts = 0;
  const timer = setInterval(() => {
    runV16HeaderFixes();
    attempts += 1;
    if (attempts >= 20 || document.querySelector('[data-elorene-currency-pinned="true"]')) clearInterval(timer);
  }, 500);

  const observer = new MutationObserver(() => {
    if (!document.querySelector('[data-elorene-currency-pinned="true"]')) pinCurrencySwitcher();
    markSaleMenu();
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

/** ELORÉNE V17: prefer the native Shopify Markets selector for the pinned currency control. */
(() => {
  'use strict';

  function pinNativeCurrency() {
    const native = document.querySelector(
      '#header-component dropdown-localization-component.dropdown-localization, #header-component .dropdown-localization:not(.mobile\\:hidden)'
    );
    if (!native) return;

    native.setAttribute('data-elorene-currency-pinned', 'true');
    const important = (name, value) => native.style.setProperty(name, value, 'important');
    important('position', 'fixed');
    important('left', '50%');
    important('right', 'auto');
    important('top', 'auto');
    important('bottom', 'max(18px, env(safe-area-inset-bottom))');
    important('transform', 'translateX(-50%)');
    important('margin', '0');
    important('z-index', '2147483000');
  }

  function run() {
    pinNativeCurrency();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run, { once: true });
  else run();

  window.addEventListener('load', run, { once: true });
  setTimeout(run, 600);
  setTimeout(run, 1600);
})();
